// Quản lý TÀI KHOẢN MÁY (admin + kiosk) — M2 refactor 2026-04-22.
// User = máy/ca (KHÔNG phải NV). Sau refactor:
//   - role ∈ {'admin','kiosk'} — bỏ 'nv'
//   - L2 TOTP dời sang Employees (NV), KHÔNG còn ở user
//   - shop_id là integer reference tới shops (tra cứu qua /admin/shops)
//
// Thao tác per-row:
//   - Pause/Activate (active ↔ paused)
//   - Disable (→ archived, soft-archive, chuẩn bị xoá)
//   - Reactivate (archived → active)
//   - Reset pass (admin nhập pass mới cho máy)
//   - Tạo/Đặt lại L1 (QR cho admin GIỮ — máy cần quét vào app Authenticator trên máy đó)
//   - Mở khoá (password-lock hoặc L1-lock)
//   - Xoá vĩnh viễn (Phương án C) — chỉ hiện khi status='archived'
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { showConfirm, showPrompt } from '../lib/dialogs.js';
import { useAuthStore } from '../lib/auth.js';
import QrModal from '../components/QrModal.jsx';

export default function AdminUsersPage() {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  // M145 (2026-05-12): edit modal — store user object đang sửa
  const [editUser, setEditUser] = useState(null);
  const [qr, setQr] = useState(null);
  const [deleting, setDeleting] = useState(null); // user object đang xoá vĩnh viễn

  async function reload() {
    setLoading(true);
    try {
      const [{ users }, { shops }] = await Promise.all([
        api.adminUsers(),
        api.adminShops(),
      ]);
      setUsers(users);
      setShops(shops);
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, []);

  const shopById = (id) => shops.find(s => s.id === id);
  const shopLabel = (id) => {
    if (id === null || id === undefined) return <span className="text-slate-300">—</span>;
    const s = shopById(id);
    return s ? <span title={s.name}>{s.code}</span> : <span className="text-rose-500">#{id}?</span>;
  };

  async function act(fn, successMsg) {
    try { await fn(); toast.success(successMsg); reload(); }
    catch (err) { toast.error(err.data?.error || err.message); }
  }

  async function doGenerateL1(u) {
    try {
      const res = await api.adminGenerateL1(u.id);
      setQr({
        open: true,
        title: `TOTP L1 cho máy ${u.username}`,
        note: u.role === 'admin'
          ? 'Admin giữ chuỗi bí mật L1 cho tài khoản admin này. Nhập mã 6 số xác nhận để lưu.'
          : 'Đây là L1 của MÁY kiosk — quét QR vào app Authenticator trên CHÍNH máy đó (không phải điện thoại NV). Nhập mã 6 số để xác nhận.',
        qrPng: res.qr_png,
        secretBase32: res.secret_base32,
        otpauthUri: res.otpauth_uri,
        warningTone: 'warn',
        onVerify: async (code) => { await api.adminVerifyL1(u.id, res.secret_base32, code); },
        onVerified: () => { setQr(null); toast.success('Đã lưu L1 mới — giờ máy có thể login bằng mã từ Authenticator'); reload(); },
      });
    } catch (err) { toast.error(err.data?.error || err.message); }
  }

  function anyLock(u) {
    return !!(u.locked_until || u.l1_locked_until);
  }

  return (
    <div className="min-h-full p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Trang admin</Link>
        <div className="flex items-start justify-between gap-3 mt-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-brand-700">Quản lý máy kiosk & admin</h1>
            <p className="text-xs text-slate-500 mt-1">
              User = máy / ca trực (không phải NV). L2 TOTP của NV quản lý tại{' '}
              <Link to="/admin/employees" className="underline text-brand-600">Nhân viên</Link>.
            </p>
          </div>
          <button onClick={() => setShowCreate(true)} className="tap-feedback px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium shadow-sm w-full sm:w-auto">
            + Tạo máy mới
          </button>
        </div>

        <div className="mt-4 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                {/* M148b: hide ID + Hiển thị + L1 + Khoá mobile (xem chi tiết qua Sửa) */}
                <th className="px-3 py-2 hidden md:table-cell">ID</th>
                <th className="px-3 py-2">Username</th>
                <th className="px-3 py-2 hidden md:table-cell">Hiển thị</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2 hidden md:table-cell">Shop</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 hidden md:table-cell">L1</th>
                <th className="px-3 py-2 hidden md:table-cell">Khoá</th>
                <th className="px-3 py-2 text-right">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">Đang tải…</td></tr>
              ) : users.map(u => {
                const isSelf = currentUser && u.id === currentUser.id;
                const isArchived = u.status === 'archived';
                return (
                  <tr key={u.id} className={`border-t border-slate-100 align-top ${isArchived ? 'bg-slate-50/60 text-slate-500' : ''}`}>
                    <td className="px-3 py-2 font-mono text-xs hidden md:table-cell">{u.id}</td>
                    <td className="px-3 py-2 font-medium">
                      {u.username}
                      {isSelf && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-sky-100 text-sky-700 align-middle">bạn</span>}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell">{u.display_name}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${u.role === 'admin' ? 'bg-indigo-100 text-indigo-700' : 'bg-sky-100 text-sky-700'}`}>
                        {u.role === 'admin' ? 'Admin' : 'Kiosk'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">{shopLabel(u.shop_id)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${u.status === 'active' ? 'bg-emerald-100 text-emerald-700' : u.status === 'paused' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell">{u.has_l1 ? <span className="text-emerald-600">✅</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">
                      {anyLock(u) ? <LockBadges u={u} /> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap gap-1 justify-end">
                        {isArchived ? (
                          <>
                            <button
                              onClick={() => act(() => api.adminUpdateUser(u.id, { status: 'active' }), 'Đã kích hoạt lại')}
                              className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                              title="Đưa tài khoản trở lại trạng thái hoạt động"
                            >
                              Kích hoạt lại
                            </button>
                            {!isSelf && (
                              <button
                                onClick={() => setDeleting(u)}
                                className="text-xs px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700"
                                title="Xoá vĩnh viễn tài khoản (yêu cầu TOTP admin + gõ lại username)"
                              >
                                Xoá vĩnh viễn
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            {u.status === 'active' ? (
                              isSelf ? (
                                <span
                                  className="text-[11px] px-2 py-1 rounded bg-sky-50 text-sky-600 italic border border-sky-200"
                                  title="Không thể tự tạm dừng tài khoản đang đăng nhập — nhờ admin khác, hoặc đăng xuất rồi chạy scripts/recover-admin.js"
                                >
                                  tài khoản của bạn
                                </span>
                              ) : (
                                <button onClick={() => act(() => api.adminUpdateUser(u.id, { status: 'paused' }), 'Đã tạm dừng')} className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200">Pause</button>
                              )
                            ) : (
                              <button onClick={() => act(() => api.adminUpdateUser(u.id, { status: 'active' }), 'Đã kích hoạt')} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Activate</button>
                            )}
                            <button onClick={async () => {
                              const p = await showPrompt({
                                title: 'Reset mật khẩu',
                                message: `Mật khẩu mới cho "${u.username}" (≥ 8 ký tự):`,
                                placeholder: 'Mật khẩu mới',
                                inputType: 'text',
                                confirmText: 'Reset',
                              });
                              if (p && p.length >= 8) act(() => api.adminResetPassword(u.id, p), 'Đã reset mật khẩu');
                              else if (p) toast.error('Mật khẩu quá ngắn (cần ≥ 8 ký tự)');
                            }} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200">Reset pass</button>
                            <button onClick={() => doGenerateL1(u)} className="text-xs px-2 py-1 rounded bg-indigo-100 text-indigo-700 hover:bg-indigo-200">
                              {u.has_l1 ? 'Đặt lại L1' : 'Tạo L1'}
                            </button>
                            {/* M145 (2026-05-12): nút Sửa role + permissions */}
                            <button onClick={() => setEditUser(u)} className="text-xs px-2 py-1 rounded bg-sky-100 text-sky-700 hover:bg-sky-200">
                              Sửa
                            </button>
                            {anyLock(u) && (
                              <button onClick={() => act(() => api.adminUnlock(u.id), 'Đã mở khoá')} className="text-xs px-2 py-1 rounded bg-rose-100 text-rose-700 hover:bg-rose-200">Mở khoá</button>
                            )}
                            {!isSelf && (
                              <button
                                onClick={async () => {
                                  const ok = await showConfirm({
                                    title: 'Disable tài khoản',
                                    message: `Disable (archive) tài khoản "${u.username}"?\n\nSau khi disable, có thể:\n• Kích hoạt lại → về trạng thái active\n• Xoá vĩnh viễn → xoá khỏi DB (không hoàn tác)`,
                                    confirmText: 'Disable',
                                    dangerous: true,
                                  });
                                  if (ok) {
                                    act(() => api.adminUpdateUser(u.id, { status: 'archived' }), 'Đã disable');
                                  }
                                }}
                                className="text-xs px-2 py-1 rounded bg-slate-200 text-slate-700 hover:bg-rose-100 hover:text-rose-700"
                                title="Archive tài khoản — bước trước khi xoá vĩnh viễn"
                              >
                                Disable
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {showCreate && <CreateUserModal shops={shops} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); reload(); }} />}

        {/* M145 (2026-05-12): edit role + permissions */}
        {editUser && (
          <EditUserModal
            user={editUser}
            shops={shops}
            selfId={currentUser?.id}
            onClose={() => setEditUser(null)}
            onSaved={() => { setEditUser(null); reload(); }}
          />
        )}

        {qr?.open && (
          <QrModal
            open
            onClose={() => setQr(null)}
            title={qr.title}
            note={qr.note}
            qrPng={qr.qrPng}
            secretBase32={qr.secretBase32}
            otpauthUri={qr.otpauthUri}
            warningTone={qr.warningTone}
            onVerify={qr.onVerify}
            onVerified={qr.onVerified}
          />
        )}

        {deleting && (
          <HardDeleteUserModal
            user={deleting}
            onClose={() => setDeleting(null)}
            onDeleted={() => { setDeleting(null); toast.success(`Đã xoá vĩnh viễn tài khoản "${deleting.username}"`); reload(); }}
          />
        )}
      </div>
    </div>
  );
}

function LockBadges({ u }) {
  const fmt = t => t ? new Date(t).toLocaleTimeString('vi-VN') : null;
  const list = [
    u.locked_until    && `PW→${fmt(u.locked_until)}`,
    u.l1_locked_until && `L1→${fmt(u.l1_locked_until)}`,
  ].filter(Boolean);
  return (
    <div className="space-y-0.5">
      {list.map((s, i) => (
        <div key={i} className="text-rose-700 bg-rose-50 rounded px-1.5 py-0.5 inline-block mr-1">{s}</div>
      ))}
    </div>
  );
}

function CreateUserModal({ shops, onClose, onCreated }) {
  const activeShops = (shops || []).filter(s => s.status === 'active');
  const [form, setForm] = useState({
    username: '',
    password: '',
    display_name: '',
    role: 'kiosk',
    shop_id: activeShops[0]?.id ?? null,
    permissions: [],   // M144a: chỉ dùng khi role='manager'
  });
  const [busy, setBusy] = useState(false);
  // M144a: load permissions metadata khi mở modal (1 lần)
  const [permGroups, setPermGroups] = useState([]);
  useEffect(() => {
    api.adminPermissionGroups().then(r => setPermGroups(r.groups || [])).catch(() => {});
  }, []);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        username: form.username,
        password: form.password,
        display_name: form.display_name,
        role: form.role,
        shop_id: (form.role === 'admin' || form.role === 'manager') ? null : Number(form.shop_id),
        ...(form.role === 'manager' ? { permissions: form.permissions } : {}),
      };
      await api.adminCreateUser(body);
      toast.success(
        form.role === 'manager'
          ? 'Tạo Quản lý thành công — nhớ bấm "Tạo L1" để login + chỉnh quyền sau qua nút Sửa'
          : 'Tạo máy thành công — nhớ bấm "Tạo L1" ở hàng máy để login được'
      );
      onCreated();
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  function togglePerm(permKey) {
    setForm(f => {
      const has = f.permissions.includes(permKey);
      return { ...f, permissions: has ? f.permissions.filter(p => p !== permKey) : [...f.permissions, permKey] };
    });
  }

  function toggleGroup(group, allChecked) {
    setForm(f => {
      const groupKeys = group.permissions.map(p => p.key);
      const others = f.permissions.filter(p => !groupKeys.includes(p));
      return { ...f, permissions: allChecked ? others : [...others, ...groupKeys] };
    });
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[92vh] overflow-auto p-6 space-y-3">
        <h3 className="font-semibold text-lg">Tạo máy / tài khoản mới</h3>
        <Field label="Username (ví dụ: kiosk_hp_1)" value={form.username} onChange={update('username')} />
        <Field label="Mật khẩu (≥8 ký tự)" value={form.password} onChange={update('password')} type="password" />
        <Field label="Tên hiển thị" value={form.display_name} onChange={update('display_name')} />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Vai trò"
            value={form.role}
            onChange={update('role')}
            options={[
              ['kiosk','Kiosk (máy NV)'],
              ['manager','Quản lý (phân quyền)'],
              ['admin','Admin (toàn quyền)'],
            ]}
          />
          {form.role === 'kiosk' ? (
            <Select
              label="Shop"
              value={form.shop_id ?? ''}
              onChange={(e) => setForm(f => ({ ...f, shop_id: e.target.value ? Number(e.target.value) : null }))}
              options={activeShops.length
                ? activeShops.map(s => [s.id, `${s.code} — ${s.name}`])
                : [['', '(chưa có shop — tạo shop trước)']]}
            />
          ) : (
            <div className="text-xs text-slate-500 self-end pb-2">
              {form.role === 'admin' ? 'Admin: toàn quyền, không gắn shop' : 'Quản lý: cross-shop, không gắn shop'}
            </div>
          )}
        </div>

        {/* M144a: permissions checkbox grid — chỉ hiện khi role='manager' */}
        {form.role === 'manager' && (
          <div className="border rounded-lg p-3 bg-slate-50 space-y-3">
            <div className="text-sm font-semibold text-slate-700">Phân quyền Quản lý</div>
            {permGroups.length === 0 && <div className="text-sm text-slate-500">Đang tải danh sách quyền...</div>}
            {permGroups.map(group => {
              const checkedKeys = group.permissions.filter(p => form.permissions.includes(p.key));
              const allChecked = checkedKeys.length === group.permissions.length && group.permissions.length > 0;
              const someChecked = checkedKeys.length > 0 && !allChecked;
              return (
                <div key={group.key} className="bg-white rounded border p-2">
                  <label className="flex items-center gap-2 mb-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked; }}
                      onChange={() => toggleGroup(group, allChecked)}
                    />
                    <span className="text-sm font-medium text-slate-800">{group.label}</span>
                    <span className="text-xs text-slate-400">({checkedKeys.length}/{group.permissions.length})</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1 pl-6">
                    {group.permissions.map(p => (
                      <label key={p.key} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={form.permissions.includes(p.key)}
                          onChange={() => togglePerm(p.key)}
                        />
                        <span className="text-slate-700">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="text-xs text-slate-500">
              Đã chọn <strong>{form.permissions.length}</strong> quyền. Admin có thể chỉnh quyền sau qua nút "Sửa" ở danh sách.
            </div>
          </div>
        )}

        <div className="text-xs text-amber-700 bg-amber-50 rounded p-2 border border-amber-200">
          Sau khi tạo, tài khoản <strong>chưa có TOTP L1</strong> — bấm "Tạo L1" ở hàng để quét QR vào app Authenticator.
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium">Huỷ</button>
          <button type="submit" disabled={busy || (form.role === 'kiosk' && !form.shop_id)} className="flex-1 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? 'Đang tạo…' : 'Tạo'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// M145 (2026-05-12): EditUserModal — sửa role + permissions + display_name + shop_id
// ============================================================
function EditUserModal({ user, shops, selfId, onClose, onSaved }) {
  const activeShops = (shops || []).filter(s => s.status === 'active');
  const isSelf = user.id === selfId;
  const [form, setForm] = useState({
    display_name: user.display_name || '',
    role: user.role,
    shop_id: user.shop_id,
    permissions: Array.isArray(user.permissions) ? [...user.permissions] : [],
  });
  const [busy, setBusy] = useState(false);
  const [permGroups, setPermGroups] = useState([]);

  useEffect(() => {
    api.adminPermissionGroups().then(r => setPermGroups(r.groups || [])).catch(() => {});
  }, []);

  function togglePerm(permKey) {
    setForm(f => {
      const has = f.permissions.includes(permKey);
      return { ...f, permissions: has ? f.permissions.filter(p => p !== permKey) : [...f.permissions, permKey] };
    });
  }
  function toggleGroup(group, allChecked) {
    setForm(f => {
      const groupKeys = group.permissions.map(p => p.key);
      const others = f.permissions.filter(p => !groupKeys.includes(p));
      return { ...f, permissions: allChecked ? others : [...others, ...groupKeys] };
    });
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        display_name: form.display_name,
      };
      // Đổi role chỉ khi khác hiện tại + không phải self
      if (form.role !== user.role && !isSelf) body.role = form.role;
      // Shop_id: chỉ áp khi role mới = kiosk hoặc giữ role cũ kiosk
      const effectiveRole = body.role || user.role;
      if (effectiveRole === 'kiosk') {
        body.shop_id = form.shop_id ? Number(form.shop_id) : null;
      } else {
        body.shop_id = null;
      }
      // Permissions: chỉ gửi khi role hiệu lực = manager
      if (effectiveRole === 'manager') {
        body.permissions = form.permissions;
      }
      await api.adminUpdateUser(user.id, body);
      toast.success('Đã lưu thay đổi');
      onSaved();
    } catch (err) {
      toast.error(err?.data?.error || err.message || 'Lỗi lưu');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[92vh] overflow-auto p-6 space-y-3">
        <h3 className="font-semibold text-lg">Sửa tài khoản: <span className="text-slate-600 font-mono">{user.username}</span></h3>
        <Field label="Tên hiển thị" value={form.display_name} onChange={(e) => setForm(f => ({ ...f, display_name: e.target.value }))} />
        <div className="grid grid-cols-2 gap-3">
          <Select
            label={isSelf ? 'Vai trò (không thể tự đổi)' : 'Vai trò'}
            value={form.role}
            onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
            disabled={isSelf}
            options={[
              ['kiosk','Kiosk (máy NV)'],
              ['manager','Quản lý (phân quyền)'],
              ['admin','Admin (toàn quyền)'],
            ]}
          />
          {form.role === 'kiosk' ? (
            <Select
              label="Shop"
              value={form.shop_id ?? ''}
              onChange={(e) => setForm(f => ({ ...f, shop_id: e.target.value ? Number(e.target.value) : null }))}
              options={activeShops.length
                ? activeShops.map(s => [s.id, `${s.code} — ${s.name}`])
                : [['', '(chưa có shop)']]}
            />
          ) : (
            <div className="text-xs text-slate-500 self-end pb-2">
              {form.role === 'admin' ? 'Admin: toàn quyền, không gắn shop' : 'Quản lý: cross-shop, không gắn shop'}
            </div>
          )}
        </div>

        {form.role === 'manager' && (
          <div className="border rounded-lg p-3 bg-slate-50 space-y-3">
            <div className="text-sm font-semibold text-slate-700">Phân quyền Quản lý</div>
            {permGroups.length === 0 && <div className="text-sm text-slate-500">Đang tải danh sách quyền...</div>}
            {permGroups.map(group => {
              const checkedKeys = group.permissions.filter(p => form.permissions.includes(p.key));
              const allChecked = checkedKeys.length === group.permissions.length && group.permissions.length > 0;
              const someChecked = checkedKeys.length > 0 && !allChecked;
              return (
                <div key={group.key} className="bg-white rounded border p-2">
                  <label className="flex items-center gap-2 mb-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked; }}
                      onChange={() => toggleGroup(group, allChecked)}
                    />
                    <span className="text-sm font-medium text-slate-800">{group.label}</span>
                    <span className="text-xs text-slate-400">({checkedKeys.length}/{group.permissions.length})</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1 pl-6">
                    {group.permissions.map(p => (
                      <label key={p.key} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => togglePerm(p.key)} />
                        <span className="text-slate-700">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="text-xs text-slate-500">
              Đã chọn <strong>{form.permissions.length}</strong> quyền. Thay đổi có hiệu lực ngay sau khi save.
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium">Huỷ</button>
          <button type="submit" disabled={busy} className="flex-1 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? 'Đang lưu…' : 'Lưu thay đổi'}
          </button>
        </div>
      </form>
    </div>
  );
}

function HardDeleteUserModal({ user, onClose, onDeleted }) {
  const [usernameConfirm, setUsernameConfirm] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [busy, setBusy] = useState(false);

  const usernameOk = usernameConfirm.trim() === user.username;
  const totpOk = /^\d{6}$/.test(totpCode.trim());
  const canSubmit = usernameOk && totpOk && !busy;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.adminHardDeleteUser({
        userId: user.id,
        usernameConfirm: usernameConfirm.trim(),
        totpCode: totpCode.trim(),
      });
      onDeleted();
    } catch (err) {
      toast.error(err.data?.error || err.message);
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
        <div>
          <h3 className="font-semibold text-lg text-rose-700">⚠️ Xoá vĩnh viễn tài khoản</h3>
          <p className="text-xs text-slate-600 mt-1">
            Thao tác này <strong>KHÔNG THỂ HOÀN TÁC</strong>. Mọi refresh token & L2 tạm gắn với tài khoản sẽ bị revoke.
            Audit log vẫn giữ lịch sử hành động trước đây (user_id → NULL).
          </p>
        </div>

        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm">
          <div className="font-medium text-rose-800">Sắp xoá:</div>
          <div className="mt-1 font-mono text-rose-900">{user.username}</div>
          <div className="text-xs text-rose-700">{user.display_name} · {user.role === 'admin' ? 'Admin' : 'Kiosk'} · ID #{user.id}</div>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700">
            Gõ lại username để xác nhận: <span className="font-mono text-rose-700">{user.username}</span>
          </span>
          <input
            autoFocus
            value={usernameConfirm}
            onChange={(e) => setUsernameConfirm(e.target.value)}
            placeholder={user.username}
            className={`mt-1 w-full px-3 py-2 rounded-lg border outline-none font-mono ${
              usernameConfirm.length === 0 ? 'border-slate-300' :
              usernameOk ? 'border-emerald-400 bg-emerald-50' : 'border-rose-400 bg-rose-50'
            }`}
          />
          {usernameConfirm.length > 0 && !usernameOk && (
            <span className="text-xs text-rose-600 mt-1 block">Chưa khớp</span>
          )}
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700">
            Mã TOTP admin (6 số từ Authenticator của bạn)
          </span>
          <input
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            inputMode="numeric"
            maxLength={6}
            className={`mt-1 w-full px-3 py-2 rounded-lg border outline-none font-mono tracking-widest text-center text-lg ${
              totpCode.length === 0 ? 'border-slate-300' :
              totpOk ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300'
            }`}
          />
        </label>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={busy} className="flex-1 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium disabled:opacity-50">
            Huỷ
          </button>
          <button type="submit" disabled={!canSubmit} className="flex-1 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? 'Đang xoá…' : 'Xoá vĩnh viễn'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <input required className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none" {...rest} />
    </label>
  );
}

function Select({ label, value, onChange, options, disabled = false }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700">{label}</span>
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={`mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 ${disabled ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
      >
        {options.map(([v, l]) => <option key={String(v)} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
